import {
  CURRENCY,
  NUMBER_SEPARATORS,
  type DateFormat,
  type NumberFormat,
} from '../constants/settings';

/** The minus sign used in the UI kit (U+2212), more readable than a hyphen next to digits. */
export const MINUS_SIGN = '\u2212';

export interface FormatNumberOptions {
  decimals: number;
  format: NumberFormat;
  /** `always` shows a + on positive values (stock going in). */
  signDisplay?: 'auto' | 'always';
}

const plainFormatters = new Map<number, Intl.NumberFormat>();
const plainFormatter = (decimals: number): Intl.NumberFormat => {
  let formatter = plainFormatters.get(decimals);
  if (!formatter) {
    formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: false,
    });
    plainFormatters.set(decimals, formatter);
  }
  return formatter;
};

/** Formats a number with the company separators, for example `48 215,300`. */
export function formatNumber(value: number, options: FormatNumberOptions): string {
  if (!Number.isFinite(value)) return '—';
  const { decimals, format, signDisplay = 'auto' } = options;
  const { group, decimal } = NUMBER_SEPARATORS[format];

  const digits = plainFormatter(decimals).format(Math.abs(value));
  const isZero = Number(digits) === 0;
  const [integerPart = '0', fractionPart] = digits.split('.');
  const grouped = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, group);
  const body = fractionPart ? `${grouped}${decimal}${fractionPart}` : grouped;

  if (isZero) return body;
  if (value < 0) return `${MINUS_SIGN}${body}`;
  return signDisplay === 'always' ? `+${body}` : body;
}

/** Money in TND with 3 decimals, for example `48 215,300 TND`. */
export const formatMoney = (value: number, format: NumberFormat): string =>
  `${formatNumber(value, { decimals: CURRENCY.decimals, format })} ${CURRENCY.code}`;

// ---------------------------------------------------------------- Dates

export interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const partFormatters = new Map<string, Intl.DateTimeFormat>();
const partFormatter = (timeZone: string): Intl.DateTimeFormat => {
  let formatter = partFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    partFormatters.set(timeZone, formatter);
  }
  return formatter;
};

/** Calendar parts of an instant, as seen in the given IANA time zone. */
export function zonedParts(date: Date | string, timeZone: string): ZonedParts {
  const instant = typeof date === 'string' ? new Date(date) : date;
  const parts: Record<string, number> = {};
  for (const part of partFormatter(timeZone).formatToParts(instant)) {
    if (part.type !== 'literal') parts[part.type] = Number(part.value);
  }
  return {
    year: parts.year ?? 0,
    month: parts.month ?? 0,
    day: parts.day ?? 0,
    hour: parts.hour ?? 0,
    minute: parts.minute ?? 0,
    second: parts.second ?? 0,
  };
}

const pad = (value: number, length = 2) => String(value).padStart(length, '0');

export function formatDate(date: Date | string, timeZone: string, dateFormat: DateFormat): string {
  const { year, month, day } = zonedParts(date, timeZone);
  switch (dateFormat) {
    case 'MM/DD/YYYY':
      return `${pad(month)}/${pad(day)}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${pad(month)}-${pad(day)}`;
    case 'DD/MM/YYYY':
    default:
      return `${pad(day)}/${pad(month)}/${year}`;
  }
}

/** 24-hour time, for example `15:45`. */
export function formatTime(date: Date | string, timeZone: string, withSeconds = false): string {
  const { hour, minute, second } = zonedParts(date, timeZone);
  return withSeconds ? `${pad(hour)}:${pad(minute)}:${pad(second)}` : `${pad(hour)}:${pad(minute)}`;
}

export const formatDateTime = (
  date: Date | string,
  timeZone: string,
  dateFormat: DateFormat,
): string => `${formatDate(date, timeZone, dateFormat)} ${formatTime(date, timeZone)}`;

/** `YYYY-MM-DD` of the calendar day in the given zone, handy to compare days. */
export function zonedDayKey(date: Date | string, timeZone: string): string {
  const { year, month, day } = zonedParts(date, timeZone);
  return `${year}-${pad(month)}-${pad(day)}`;
}

export const zonedYear = (date: Date | string, timeZone: string): number =>
  zonedParts(date, timeZone).year;

/** A day told after the fact counts at its noon: it stays that day in the company's reports. */
export const zonedNoon = (day: string, timeZone: string): Date =>
  new Date(zonedDayStart(day, timeZone).getTime() + 12 * 3600 * 1000);

/**
 * The instant a calendar day starts in a time zone: "2026-10-14" in Africa/Tunis is
 * 2026-10-13T23:00Z. Used for date filters and stock at a past date.
 */
export function zonedDayStart(day: string, timeZone: string): Date {
  const [year, month, date] = day.split('-').map(Number) as [number, number, number];
  const guess = Date.UTC(year, month - 1, date);
  const parts = zonedParts(new Date(guess), timeZone);
  const shown = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour % 24,
    parts.minute,
    parts.second,
  );
  return new Date(guess - (shown - guess));
}

/** The first instant after a calendar day in a time zone. */
export function zonedDayEnd(day: string, timeZone: string): Date {
  const start = zonedDayStart(day, timeZone);
  const next = zonedDayKey(new Date(start.getTime() + 36 * 3600 * 1000), timeZone);
  return zonedDayStart(next, timeZone);
}
